import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Menu, X, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../mock';

const Header = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (id === 'gallery') {
      navigate('/gallery');
      setIsMobileMenuOpen(false);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { id: 'about', label: 'About' },
    { id: 'services', label: 'Packages' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'testimonials', label: 'Reviews' },
    { id: 'vip', label: 'VIP' },
    { id: 'contact', label: 'Contact' }
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-slate-900/5 py-2'
            : 'bg-transparent py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <button
              data-testid="logo-btn"
              onClick={() => scrollToSection('hero')}
              className="flex items-center group"
            >
              <div className={`rounded-xl p-1.5 transition-all duration-500 ${
                isScrolled ? 'bg-slate-900' : 'bg-white/10 backdrop-blur-md border border-white/20'
              }`}>
                <img
                  src="https://customer-assets.emergentagent.com/job_ad632560-d0a8-4e29-a8e3-0fdc0eb21a29/artifacts/erjp1y5v_594DED26-5434-47CF-8EE8-EA9BF153DAD6.PNG"
                  alt="ProClean"
                  className="h-9 w-auto"
                />
              </div>
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  data-testid={`nav-${item.id}`}
                  onClick={() => scrollToSection(item.id)}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    isScrolled
                      ? 'text-slate-700 hover:text-blue-600 hover:bg-blue-50'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <Button
                data-testid="header-build-btn"
                onClick={() => navigate('/special-services')}
                className="ml-4 bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-slate-950 rounded-xl px-6 font-bold shadow-lg shadow-cyan-500/25"
              >
                <Zap className="w-4 h-4 mr-2" />
                Special Services
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                isScrolled ? 'text-slate-900' : 'text-white'
              }`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <nav className="lg:hidden py-4 mt-2 bg-white rounded-2xl shadow-xl border border-slate-100">
              <div className="flex flex-col px-4 space-y-1">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    data-testid={`mobile-nav-${item.id}`}
                    onClick={() => scrollToSection(item.id)}
                    className="text-slate-700 hover:text-blue-600 font-semibold transition-colors text-left px-4 py-3 rounded-lg hover:bg-blue-50"
                  >
                    {item.label}
                  </button>
                ))}
                <div className="pt-2 px-2">
                  <Button
                    data-testid="mobile-build-btn"
                    onClick={() => { navigate('/special-services'); setIsMobileMenuOpen(false); }}
                    className="bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-950 w-full rounded-xl font-bold"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Special Services
                  </Button>
                </div>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Sticky Mobile Bottom CTA */}
      <div className="lg:hidden fixed bottom-4 left-4 right-4 z-40">
        <div className="flex gap-2">
          <a
            href={`tel:${businessInfo.phone.replace(/\s/g, '')}`}
            data-testid="sticky-call-btn"
            className="flex-1 bg-slate-900 text-white py-4 rounded-2xl font-semibold flex items-center justify-center shadow-2xl shadow-slate-900/30"
          >
            <Phone className="w-5 h-5 mr-2" />
            Call
          </a>
          <button
            data-testid="sticky-book-btn"
            onClick={() => scrollToSection('booking')}
            className="flex-1 bg-blue-600 text-white py-4 rounded-2xl font-semibold shadow-2xl shadow-blue-600/40"
          >
            Book Now
          </button>
        </div>
      </div>
    </>
  );
};

export default Header;
